import { GridColDef } from "@material-ui/data-grid";
import { Box, Table, Typography } from "components";
import { compose, withHooks, withTranslation } from "enhancers";
import withPagination, { PaginationKeys } from "enhancers/withPagination";
import { PageContent } from "layouts";
import { BreadcrumbsProps } from "layouts/PageContent";
import { isEmpty } from "lodash";
import { filterEmptyValues, gql, homePath, paths } from "utils/helper";
import BudgetFilters, { budgetFilterInitialValues } from "./BudgetFilters";

const BudgetIndexPage = ({ paginationData, totalItems, ...props }: any) => (
  <PageContent
    title={props.t(".title")}
    breadcrumbs={props.breadcrumbs}
    pageActions={[]}
    filter={<BudgetFilters />}
  >
    <Box width="100%" mb={-6}>
      <Box mb={4}>
        <Typography variant="h4">{props.t(".title")}</Typography>
      </Box>
      <Table
        columns={props.columns}
        rows={props.tableData}
        loading={props.loading}
        onRowClickTo={paths.budgetEditPath}
        density="compact"
        autoHeight
        disableSelectionOnClick
        page={paginationData.page - 1}
        pageSize={paginationData.limit}
        onPageChange={paginationData.handlePageChange}
        onPageSizeChange={paginationData.handlePageSizeChange}
        rowsPerPageOptions={[25, 50, 100]}
        rowCount={totalItems}
        paginationMode="server"
      />
    </Box>
  </PageContent>
);

export const API = {
  FETCH_EMPLOYEE_YEARLY_BUDGET: gql`
    query FETCH_EMPLOYEE_YEARLY_BUDGET($filters: JSON, $pagination: JSON) {
      dashboardBudgetList(
        input: { filters: $filters, pagination: $pagination }
      ) {
        data {
          id
          employeeCode
          firstName
          lastName
          year
          budget
          dentalBudget
          companyBudget
          companyDentalBudget
        }
        pagination
      }
    }
  `,
};

const enhancer = compose(
  withPagination({
    defaultLimit: 25,
    navigateToPath: ({ queryParams }) => {
      paths.budgetPath(queryParams).push();
    },
    rowsPerPageOptions: [25, 50, 100],
  }),
  withTranslation({
    prefix: "pages.main.budget.index",
  }),
  withHooks((props: any, hooks: any) => {
    const { t, paginationData } = props;
    const { useMemo, useEffect, useUrlParam, useLazyQuery } = hooks;

    const [fetchList, { data, loading, error }] = useLazyQuery(
      API.FETCH_EMPLOYEE_YEARLY_BUDGET,
      {
        fetchPolicy: "network-only",
      }
    );

    const breadcrumbs = useMemo(
      (): BreadcrumbsProps[] => [
        { label: t(".home"), path: homePath() },
        { label: t(".title"), path: null },
      ],
      [t]
    );

    const queryParams = useUrlParam();
    useEffect(() => {
      const params = isEmpty(queryParams) ? undefined : queryParams;
      const allowedKeys = Object.keys(budgetFilterInitialValues);
      const hasInvalidKey =
        params &&
        Object.keys(params).some(
          (key) =>
            ![...allowedKeys, ...Object.keys(PaginationKeys)].includes(key)
        );

      if (hasInvalidKey || !params) {
        paths.budgetPath(filterEmptyValues(budgetFilterInitialValues)).push();
      } else {
        fetchList({
          variables: {
            filters: {
              code: params.code || "",
              fullName: params.fullName || "",
              year: params.year || "all",
            },
            pagination: {
              page: Number(params.page),
              limit: Number(params.limit),
            },
          },
        });
      }
    }, [queryParams, fetchList]);

    const totalItems = useMemo(
      () => data?.dashboardBudgetList.pagination.total,
      [data?.dashboardBudgetList.pagination.total]
    );

    const tableData = useMemo(() => {
      if (loading || error) {
        return [];
      }

      const dataItems = data?.dashboardBudgetList.data.map((yearly: any) => {
        const {
          id,
          year,
          budget,
          dentalBudget,
          employeeCode,
          firstName,
          lastName,
          companyBudget,
          companyDentalBudget,
        } = yearly;

        return {
          id,
          code: employeeCode,
          fullName:
            (isEmpty(firstName) || firstName == null) &&
            (isEmpty(lastName) || lastName == null)
              ? null
              : `${firstName || ""} ${lastName || ""}`,
          year,
          budget,
          dentalBudget,
          companyBudget,
          companyDentalBudget,
        };
      });

      return dataItems || [];
    }, [data?.dashboardBudgetList.data, error, loading]);

    const formatter = new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const columns = useMemo(
      (): GridColDef[] => [
        {
          width: 150,
          field: "code",
          headerName: t(".columns.code"),
        },
        {
          width: 300,
          field: "fullName",
          headerName: t(".columns.fullName"),
        },
        {
          width: 130,
          field: "year",
          headerName: t(".columns.year"),
        },
        {
          width: 150,
          field: "companyBudget",
          headerName: t(".columns.companyBudget"),
          type: "number",
          renderCell: (companyBudget: any) => {
            return formatter.format(companyBudget.value);
          },
        },
        {
          width: 150,
          field: "budget",
          headerName: t(".columns.budget"),
          renderCell: (budget: any) => {
            return formatter.format(budget.value);
          },
        },
        {
          width: 150,
          field: "companyDentalBudget",
          headerName: t(".columns.companyDentalBudget"),
          renderCell: (companyDentalBudget: any) => {
            return formatter.format(companyDentalBudget.value);
          },
        },
        {
          width: 150,
          field: "dentalBudget",
          headerName: t(".columns.dentalBudget"),
          renderCell: (dentalBudget: any) => {
            return formatter.format(dentalBudget.value);
          },
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [t]
    );

    return {
      breadcrumbs,
      tableData,
      loading,
      columns,
      paginationData,
      totalItems,
    };
  })
);

export default enhancer(BudgetIndexPage);
